import { useTenant } from "hooks/settings";
import { ReactNode, useMemo, useCallback, useEffect, useState } from "react";
import {
  ScrapEyeRecordingPosition,
  useListVideoPlayerGroupsQuery,
} from "src/store/api/generatedApi";
import type { MultiFeedVideoPlayerProps } from "./MultiFeedVideoPlayer";
import { MultiFeedVideoPlayer } from "./MultiFeedVideoPlayer";
import { VideoOverlayIconButton } from "./VideoOverlayIconButton";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
  Slide,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalStorageState } from "hooks/useLocalStorageState";

/**
 * Video player showing all angles for a location in a grid.
 * Displays angles ordered by: first from each group, second from each, etc., then ungrouped.
 */
export const AllAnglesVideoPlayer = ({
  location,
  storageKey = "default",
  topRightButtons,
  ...rest
}: {
  location: string;
  storageKey?: string;
  topRightButtons?: ReactNode;
} & MultiFeedVideoPlayerProps) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: videoPlayerGroupsWithAngles, isLoading: isLoadingGroups } =
    useListVideoPlayerGroupsQuery({
      tenantName,
      locationName: location,
    });

  const orderedAngles = useMemo(() => {
    if (!videoPlayerGroupsWithAngles) return [];

    const groups = videoPlayerGroupsWithAngles;
    const result: ScrapEyeRecordingPosition[] = [];
    const seenAngleIds = new Set<number>();

    // order it breadth first (first angle in each group, then next group)
    // group[0].angles[0], group[1].angles[0], group[0].angles[1]...
    // An angle can belong to several groups; show it once, at its earliest slot.

    const maxDepth = Math.max(0, ...groups.map((g) => g.angles.length));
    const sortedAnglesByGroup = groups.map((group) =>
      group.angles.slice().sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    );

    for (let depth = 0; depth < maxDepth; depth++) {
      for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
        const group = groups[groupIndex];
        if (!group || group.angles.length <= depth) {
          continue;
        }
        const sortedAngles = sortedAnglesByGroup[groupIndex];
        const angle = sortedAngles?.[depth];
        if (angle && !seenAngleIds.has(angle.id)) {
          seenAngleIds.add(angle.id);
          result.push(angle);
        }
      }
    }

    return result;
  }, [videoPlayerGroupsWithAngles]);

  const allAngleIds = useMemo(
    () => orderedAngles.map((angle) => angle.id),
    [orderedAngles]
  );

  const storageKeyFull = `angleSelection_${storageKey}_${tenantName}_${location}`;

  const [storedAngleIds, setStoredAngleIds] = useLocalStorageState<
    number[] | null
  >(storageKeyFull, null);

  // Select all angles by default when data first loads and no prior selection exists
  useEffect(() => {
    if (storedAngleIds === null && allAngleIds.length > 0) {
      setStoredAngleIds(allAngleIds);
    }
  }, [allAngleIds, storedAngleIds, setStoredAngleIds]);

  // Filter stored IDs to only include valid angle IDs
  useEffect(() => {
    if (storedAngleIds === null || allAngleIds.length === 0) return;
    const validIds = storedAngleIds.filter((id) => allAngleIds.includes(id));
    if (validIds.length !== storedAngleIds.length) {
      setStoredAngleIds(validIds);
    }
  }, [allAngleIds, storedAngleIds, setStoredAngleIds]);

  const selectedAngleIdsArray = useMemo(
    () => storedAngleIds ?? [],
    [storedAngleIds]
  );

  const selectedAngleIds = useMemo(
    () => new Set(selectedAngleIdsArray),
    [selectedAngleIdsArray]
  );

  const filteredAngles = useMemo(
    () => orderedAngles.filter((angle) => selectedAngleIds.has(angle.id)),
    [orderedAngles, selectedAngleIds]
  );

  const toggleAngle = useCallback(
    (angleId: number) => {
      if (selectedAngleIds.has(angleId)) {
        setStoredAngleIds(selectedAngleIdsArray.filter((id) => id !== angleId));
      } else {
        setStoredAngleIds([...selectedAngleIdsArray, angleId]);
      }
    },
    [selectedAngleIds, selectedAngleIdsArray, setStoredAngleIds]
  );

  const toggleAll = useCallback(() => {
    if (selectedAngleIds.size === allAngleIds.length) {
      setStoredAngleIds([]);
    } else {
      setStoredAngleIds(allAngleIds);
    }
  }, [selectedAngleIds.size, allAngleIds, setStoredAngleIds]);

  const source = useMemo(
    () => ({
      mode: "angle" as const,
      angles: filteredAngles,
    }),
    [filteredAngles]
  );

  const allSelected = selectedAngleIds.size === allAngleIds.length;
  const someSelected =
    selectedAngleIds.size > 0 && selectedAngleIds.size < allAngleIds.length;

  return (
    <Box sx={{ position: "relative", overflow: "hidden" }}>
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1000,
        }}
      >
        <Stack direction="row" spacing={1}>
          {topRightButtons}
          <Tooltip title={t("selectAnglesToDisplay")}>
            <VideoOverlayIconButton
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <TuneIcon />
            </VideoOverlayIconButton>
          </Tooltip>
        </Stack>
      </Box>
      <MultiFeedVideoPlayer
        source={source}
        isLoading={isLoadingGroups}
        {...rest}
      />

      {drawerOpen ? (
        <Box
          onClick={() => {
            setDrawerOpen(false);
          }}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1001,
          }}
        />
      ) : null}

      <Slide direction="left" in={drawerOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: 300,
            backgroundColor: "background.paper",
            boxShadow: 3,
            zIndex: 1002,
            p: 2,
            overflowY: "auto",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6">{t("selectAngles")}</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
              }
              label={t("selectAll")}
            />
            <Stack spacing={1}>
              {orderedAngles.map((angle) => (
                <FormControlLabel
                  key={angle.id}
                  control={
                    <Checkbox
                      checked={selectedAngleIds.has(angle.id)}
                      onChange={() => {
                        toggleAngle(angle.id);
                      }}
                    />
                  }
                  label={`${angle.name} (${angle.location_name})`}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      </Slide>
    </Box>
  );
};
